import { useEffect, useState } from 'react'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import type { EmotionStimulus } from '@/services/emotionAnalysisService'
import type { EmotionKey } from '@/components/researcher/liveDemo/constants'
import StimulusStage from './StimulusStage'
import FaceStage from './FaceStage'
import FacePip from './FacePip'
import GazeDot from './GazeDot'
import GazeHeatLayer, { type HeatField } from './GazeHeatLayer'
import { stageFrameSx, STAGE_ASPECT } from './stageFrame'
import type { PrototypeReplayData } from './PrototypeReplay'

/** Above this ratio an enlarged recording already fills most of the frame, so
 *  there is no room beside it for the stimulus and it goes back to a corner
 *  pip. Below it (portrait and near-square captures) the pillarbox would be
 *  wider than the pip — that space carries the stimulus instead. */
const SIDE_BY_SIDE_MAX_ASPECT = 1.25

/**
 * FaceTime-style two-view player: the stimulus fills the stage with the
 * respondent's face as a corner pip — clicking the pip swaps them, putting the
 * face front-and-centre (with emotion rectangle/colors, see `FaceStage`) and
 * shrinking the stimulus into the corner. Clicking the stimulus tile swaps back.
 *
 * Neither view sizes itself from its media: both render into the shared 16:9
 * player frame (see `stageFrame`), so the swap never changes the player's
 * height and a portrait or 4:3 face recording can't push the transport bar and
 * timeline off screen. What the swap does change is what fills the frame —
 * the face alone with the stimulus as a corner pip, or, when the face is narrow
 * enough to leave real room beside it, the face at full frame height with the
 * stimulus in the space it doesn't use.
 *
 * Clock ownership is unchanged by the swap: the stimulus <video> keeps driving
 * the playhead (`onTick`) and carrying the audio even while rendered as the
 * small tile, so both views stay in sync and nothing re-buffers on the panel's
 * side. `stageCurrentMs` is the stage's clip-local clock while `faceCurrentMs`
 * is the clock the face recording syncs/reads emotions on — they differ only in
 * the researcher panel's merged-reel mode (see EmotionAnalysisPanel).
 */
export default function SwappableStage({
  stimulus,
  stageCurrentMs,
  playing,
  muted,
  label,
  onTick,
  onEnded,
  onVideoFailed,
  onTogglePlay,
  showFace = true,
  faceSrc,
  faceCurrentMs,
  faceOffsetMs = 0,
  series,
  gridMs,
  faceLabels,
  gazePoint = null,
  heatField = null,
  prototypeReplay = null,
}: {
  stimulus: EmotionStimulus | null
  /** Clip-local playhead for the stimulus stage. */
  stageCurrentMs: number
  playing: boolean
  muted?: boolean
  label?: string
  onTick?: (ms: number) => void
  onEnded?: () => void
  onVideoFailed?: () => void
  onTogglePlay?: () => void
  /** When false, no pip is shown and the view can't swap (e.g. aggregate mode). */
  showFace?: boolean
  faceSrc: string | null
  /** Playhead the face recording syncs on (global clock in merged mode). */
  faceCurrentMs: number
  /** Where the current stimulus begins inside the face recording. */
  faceOffsetMs?: number
  series?: Record<EmotionKey, (number | null)[]> | null
  gridMs?: number[]
  faceLabels?: Record<EmotionKey, string>
  /** Gaze position (normalised 0..1) drawn over the stimulus at the current
   *  playhead, or null to hide the dot (no gaze captured / aggregate mode). */
  gazePoint?: { x: number; y: number } | null
  /** Gaze heat map over the stimulus (a density grid from `gazeHeat.ts`), or
   *  null to draw none. */
  heatField?: HeatField | null
  /** Figma prototype (Z-98): what the stimulus view replays. */
  prototypeReplay?: PrototypeReplayData | null
}) {
  const [swapped, setSwapped] = useState(false)
  // Natural aspect ratio (w/h) of the face recording, reported by whichever
  // view is mounted once it reads metadata (the pip normally gets there first,
  // but a swap before the pip loaded leaves FaceStage to report it). Portrait
  // captures (mobile front cameras) drive the side-by-side layout; null until
  // measured, which just means the plain overlay layout for now.
  const [faceAspect, setFaceAspect] = useState<number | null>(null)
  // Splitting the frame in two only pays off where there is width to split.
  const wideEnoughToSplit = useMediaQuery(useTheme().breakpoints.up('md'))

  // Losing the face (drill-out, filter change) collapses back to the stimulus view.
  useEffect(() => {
    if (!showFace) setSwapped(false)
  }, [showFace])

  if (!swapped || !showFace) {
    return (
      <StimulusStage
        stimulus={stimulus}
        prototypeReplay={prototypeReplay}
        currentMs={stageCurrentMs}
        playing={playing}
        muted={muted}
        label={label}
        onTick={onTick}
        onEnded={onEnded}
        onVideoFailed={onVideoFailed}
        onTogglePlay={onTogglePlay}
        overlay={
          gazePoint || heatField || showFace ? (
            <>
              {heatField && <GazeHeatLayer field={heatField} />}
              {gazePoint && <GazeDot x={gazePoint.x} y={gazePoint.y} />}
              {showFace && (
                <FacePip
                  src={faceSrc}
                  currentMs={faceCurrentMs}
                  offsetMs={faceOffsetMs}
                  playing={playing}
                  series={series}
                  gridMs={gridMs}
                  labels={faceLabels}
                  onClick={() => setSwapped(true)}
                  onAspect={setFaceAspect}
                />
              )}
            </>
          ) : undefined
        }
      />
    )
  }

  // A narrow (portrait, near-square) recording fitted to the frame's height
  // leaves more empty frame beside it than a corner pip would cover, so the
  // stimulus moves into that space: the face keeps the full frame height at its
  // own shape, the stimulus takes the width the face doesn't use and sits
  // centred in it. The row is the same frame — and the same height — as every
  // other view, so this is a rearrangement, not a resize.
  if (faceAspect != null && faceAspect < SIDE_BY_SIDE_MAX_ASPECT && wideEnoughToSplit) {
    return (
      <Box sx={{ ...stageFrameSx, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <FaceStage
          src={faceSrc}
          currentMs={faceCurrentMs}
          offsetMs={faceOffsetMs}
          playing={playing}
          series={series}
          gridMs={gridMs}
          labels={faceLabels}
          onTogglePlay={onTogglePlay}
          // The share of the row a recording of this shape needs to stand
          // exactly as tall as the frame: width = H·aspect, and H = rowWidth /
          // STAGE_ASPECT, so width is `aspect / STAGE_ASPECT` of the row.
          frameWidth={`${((100 * faceAspect) / STAGE_ASPECT).toFixed(3)}%`}
          frameAspect={faceAspect}
          initialAspect={faceAspect}
          onAspect={setFaceAspect}
        />
        <Box
          onClick={(e) => {
            e.stopPropagation()
            setSwapped(false)
          }}
          role="button"
          aria-label="Przywróć pełny widok materiału"
          title="Przywróć pełny widok materiału"
          sx={{
            flex: '1 1 auto',
            minWidth: 0,
            cursor: 'pointer',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* The real stimulus player keeps running here — clock + audio owner.
              No onTogglePlay: a click on the tile swaps back instead. */}
          <StimulusStage
            stimulus={stimulus}
            prototypeReplay={prototypeReplay}
            currentMs={stageCurrentMs}
            playing={playing}
            muted={muted}
            label={label}
            onTick={onTick}
            onEnded={onEnded}
            onVideoFailed={onVideoFailed}
          />
        </Box>
      </Box>
    )
  }

  return (
    <FaceStage
      src={faceSrc}
      currentMs={faceCurrentMs}
      offsetMs={faceOffsetMs}
      playing={playing}
      series={series}
      gridMs={gridMs}
      labels={faceLabels}
      onTogglePlay={onTogglePlay}
      initialAspect={faceAspect ?? undefined}
      onAspect={setFaceAspect}
      overlay={
        <Box
          onClick={(e) => {
            e.stopPropagation()
            setSwapped(false)
          }}
          role="button"
          aria-label="Przywróć pełny widok materiału"
          title="Przywróć pełny widok materiału"
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            // Above FaceStage's click-to-play scrim (zIndex 5) so the swap-back
            // also works while paused.
            zIndex: 6,
            width: { xs: 132, md: 190 },
            cursor: 'pointer',
            borderRadius: 2,
            overflow: 'hidden',
            border: '2px solid rgba(255,255,255,.75)',
            boxShadow: '0 6px 18px rgba(0,0,0,.45)',
            transition: 'transform .15s ease, border-color .15s ease',
            '&:hover': { transform: 'scale(1.04)', borderColor: '#fff' },
          }}
        >
          {/* The real stimulus player keeps running here — clock + audio owner.
              No onTogglePlay: a click on the tile swaps back instead. */}
          <StimulusStage
            stimulus={stimulus}
            prototypeReplay={prototypeReplay}
            currentMs={stageCurrentMs}
            playing={playing}
            muted={muted}
            label={label}
            onTick={onTick}
            onEnded={onEnded}
            onVideoFailed={onVideoFailed}
          />
        </Box>
      }
    />
  )
}
